import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportPanel = ({ referrals, filters, onClose }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    date: true,
    patient: true,
    dentist: true,
    practice: true,
    teeth: true,
    reasons: true,
    status: true,
    urgency: false,
    notes: false
  });

  const handleColumnToggle = (column) => {
    setSelectedColumns(prev => ({ ...prev, [column]: !prev?.[column] }));
  };

  const handleExportCSV = () => {
    try {
      setExporting(true);

      const csvData = referrals?.map(ref => {
        const row = {};
        
        if (selectedColumns?.date) {
          row['Date de soumission'] = format(new Date(ref?.submissionDate), 'dd/MM/yyyy HH:mm', { locale: fr });
        }
        if (selectedColumns?.patient) {
          row['Patient (Initiales)'] = `${ref?.patientFirstName?.charAt(0)}${ref?.patientLastName?.charAt(0)}`;
        }
        if (selectedColumns?.dentist) {
          row['Dentiste adresseur'] = ref?.referringDentistName || '-';
        }
        if (selectedColumns?.practice) {
          row['Cabinet'] = ref?.referringPracticeName || '-';
        }
        if (selectedColumns?.teeth) {
          row['Dents'] = ref?.toothNumbers?.join(', ') || '-';
        }
        if (selectedColumns?.reasons) {
          row['Motifs'] = ref?.referralReasons?.join(', ') || '-';
        }
        if (selectedColumns?.status) {
          row['Statut'] = ref?.status || '-';
        }
        if (selectedColumns?.urgency) {
          row['Urgence'] = ref?.urgency || '-';
        }
        if (selectedColumns?.notes) {
          row['Notes cliniques'] = ref?.clinicalNotes || '-';
        }

        return row;
      });

      const csv = Papa?.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rapport-adressages-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link?.click();

      setTimeout(() => {
        setExporting(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setExporting(false);
    }
  };

  const handleExportPDF = () => {
    try {
      setExporting(true);

      const doc = new jsPDF();
      const pageWidth = doc?.internal?.pageSize?.width;
      const timestamp = format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr });

      // Header
      doc?.setFontSize(18);
      doc?.setTextColor(37, 99, 235);
      doc?.text('Rapport d\'Adressage', pageWidth / 2, 20, { align: 'center' });
      
      doc?.setFontSize(10);
      doc?.setTextColor(100);
      doc?.text(`Généré le ${timestamp}`, pageWidth / 2, 28, { align: 'center' });

      // Summary
      let yPosition = 40;
      doc?.setFontSize(12);
      doc?.setTextColor(0);
      doc?.text(`Total des adressages: ${referrals?.length}`, 14, yPosition);
      yPosition += 7;
      doc?.text(`Période: ${getFilterSummary()}`, 14, yPosition);
      yPosition += 10;

      // Table headers
      const headers = [];
      if (selectedColumns?.date) headers?.push('Date');
      if (selectedColumns?.patient) headers?.push('Patient');
      if (selectedColumns?.dentist) headers?.push('Dentiste');
      if (selectedColumns?.practice) headers?.push('Cabinet');
      if (selectedColumns?.teeth) headers?.push('Dents');
      if (selectedColumns?.reasons) headers?.push('Motifs');
      if (selectedColumns?.status) headers?.push('Statut');
      if (selectedColumns?.urgency) headers?.push('Urgence');

      // Table data
      const tableData = referrals?.map(ref => {
        const row = [];
        
        if (selectedColumns?.date) {
          row?.push(format(new Date(ref?.submissionDate), 'dd/MM/yyyy', { locale: fr }));
        }
        if (selectedColumns?.patient) {
          row?.push(`${ref?.patientFirstName?.charAt(0)}${ref?.patientLastName?.charAt(0)}`);
        }
        if (selectedColumns?.dentist) {
          row?.push(ref?.referringDentistName || '-');
        }
        if (selectedColumns?.practice) {
          row?.push(ref?.referringPracticeName || '-');
        }
        if (selectedColumns?.teeth) {
          row?.push(ref?.toothNumbers?.slice(0, 3)?.join(', ') || '-');
        }
        if (selectedColumns?.reasons) {
          row?.push(ref?.referralReasons?.[0] || '-');
        }
        if (selectedColumns?.status) {
          row?.push(ref?.status || '-');
        }
        if (selectedColumns?.urgency) {
          row?.push(ref?.urgency || '-');
        }

        return row;
      });

      doc?.autoTable({
        startY: yPosition,
        head: [headers],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [37, 99, 235] },
        styles: { fontSize: 8 }
      });

      doc?.save(`rapport-adressages-${format(new Date(), 'yyyy-MM-dd')}.pdf`);

      setTimeout(() => {
        setExporting(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setExporting(false);
    }
  };

  const getFilterSummary = () => {
    const parts = [];
    
    if (filters?.dateRange === 'custom' && filters?.customDateFrom && filters?.customDateTo) {
      parts?.push(`${format(new Date(filters?.customDateFrom), 'dd/MM/yyyy')} - ${format(new Date(filters?.customDateTo), 'dd/MM/yyyy')}`);
    } else {
      const rangeLabels = {
        last30days: 'Derniers 30 jours',
        last3months: 'Dernier trimestre',
        last6months: 'Derniers 6 mois',
        lastyear: 'Dernière année'
      };
      parts?.push(rangeLabels?.[filters?.dateRange] || 'Toutes les dates');
    }

    return parts?.join(', ');
  };

  const handleExport = () => {
    if (exportFormat === 'csv') {
      handleExportCSV();
    } else {
      handleExportPDF();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-[rgba(26,26,30,0.98)] backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Icon name="Download" size={20} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Exporter le Rapport</h2>
                <p className="text-sm text-gray-400">{referrals?.length} adressage{referrals?.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Format Selection */}
            <div>
              <label className="text-sm font-medium text-gray-200 mb-3 block">
                Format d'export
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setExportFormat('csv')}
                  className={`p-4 rounded-xl border transition-all ${
                    exportFormat === 'csv' ?'bg-blue-500/20 border-blue-500/40 text-white' :'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Icon name="FileText" size={24} className="mx-auto mb-2" />
                  <p className="text-sm font-medium">CSV</p>
                  <p className="text-xs text-gray-400 mt-1">Pour Excel/Sheets</p>
                </button>
                <button
                  onClick={() => setExportFormat('pdf')}
                  className={`p-4 rounded-xl border transition-all ${
                    exportFormat === 'pdf' ?'bg-blue-500/20 border-blue-500/40 text-white' :'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Icon name="FileText" size={24} className="mx-auto mb-2" />
                  <p className="text-sm font-medium">PDF</p>
                  <p className="text-xs text-gray-400 mt-1">Pour impression</p>
                </button>
              </div>
            </div>

            {/* Column Selection */}
            <div>
              <label className="text-sm font-medium text-gray-200 mb-3 block">
                Colonnes à inclure
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'date', label: 'Date de soumission' },
                  { key: 'patient', label: 'Patient (Initiales)' },
                  { key: 'dentist', label: 'Dentiste adresseur' },
                  { key: 'practice', label: 'Cabinet' },
                  { key: 'teeth', label: 'Dents' },
                  { key: 'reasons', label: 'Motifs d\'adressage' },
                  { key: 'status', label: 'Statut' },
                  { key: 'urgency', label: 'Urgence' },
                  { key: 'notes', label: 'Notes cliniques (CSV uniquement)' }
                ]?.map(column => (
                  <label
                    key={column?.key}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer transition-all"
                  >
                    <Checkbox
                      checked={selectedColumns?.[column?.key]}
                      onChange={() => handleColumnToggle(column?.key)}
                    />
                    <span className="text-sm text-gray-300">{column?.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Filter Summary */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-400 mb-1">Filtres appliqués</p>
                  <p className="text-sm text-gray-300">{getFilterSummary()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={exporting}
            >
              Annuler
            </Button>
            <Button
              onClick={handleExport}
              loading={exporting}
              iconName="Download"
            >
              {exporting ? 'Export en cours...' : 'Exporter'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExportPanel;